/**
 * Sticky marquee banner (`[ck-ticker]` in the Webflow Navbar component).
 *
 * The scroll itself is a CSS animation declared in the Navbar's custom-styles embed; this
 * module only prepares the DOM and manages the pause state:
 *
 * - duplicates the track's items once (aria-hidden) so the loop is seamless
 * - derives the animation duration from the content width so speed stays constant
 * - starts paused for `prefers-reduced-motion` users
 * - wires the Pause / Play toggle, which the CSS reveals on hover, focus, or while paused
 *
 * Positioning relative to the navbar lives in navScroll.ts, which owns the scroll state.
 */

const SPEED_PX_PER_SECOND = 70
const MIN_DURATION_SECONDS = 20

export default function Ticker() {
  const ticker = document.querySelector<HTMLElement>('[ck-ticker]')
  if (!ticker) return
  const track = ticker.querySelector<HTMLElement>('[ck-ticker-track]')
  const toggle = ticker.querySelector<HTMLElement>('[ck-ticker-toggle]')
  if (!track) return

  // A decorative copy of every item makes translateX(-50%) land exactly on the loop start.
  ;[...track.children].forEach((item) => {
    const clone = item.cloneNode(true) as HTMLElement
    clone.setAttribute('aria-hidden', 'true')
    clone.querySelectorAll<HTMLElement>('a, button, [tabindex]').forEach((el) => {
      el.setAttribute('tabindex', '-1')
    })
    if (clone.matches('a, button')) clone.setAttribute('tabindex', '-1')
    track.appendChild(clone)
  })

  const setDuration = () => {
    const distance = track.scrollWidth / 2
    const seconds = Math.max(MIN_DURATION_SECONDS, distance / SPEED_PX_PER_SECOND)
    ticker.style.setProperty('--ticker-duration', `${seconds.toFixed(1)}s`)
  }
  setDuration()
  addEventListener('resize', setDuration, { passive: true })
  document.fonts?.ready.then(setDuration) // widths change once the web fonts land

  const setPaused = (paused: boolean, userChoice = false) => {
    ticker.toggleAttribute('data-ck-paused', paused)
    // `data-ck-playing` lets an explicit Play override the reduced-motion default in CSS.
    ticker.toggleAttribute('data-ck-playing', userChoice && !paused)
    if (toggle) toggle.textContent = paused ? 'Play' : 'Pause'
  }

  const reduced = matchMedia('(prefers-reduced-motion: reduce)')
  setPaused(reduced.matches)
  reduced.addEventListener?.('change', (e) => setPaused(e.matches))

  if (!toggle) return
  toggle.addEventListener('click', (e) => {
    e.preventDefault()
    setPaused(!ticker.hasAttribute('data-ck-paused'), true)
  })
  // Webflow renders the toggle as a link with role="button"; give it native button keys.
  toggle.addEventListener('keydown', (e) => {
    if (e.key === ' ' && toggle.tagName !== 'BUTTON') {
      e.preventDefault()
      toggle.click()
    }
  })
}
